import { useMemo, useCallback } from 'react'
import { Flex, Text, TextField, Select } from '@radix-ui/themes'
import { ClaimFormGeoDataResponse } from '@Generated/back-office-client/src'
import { EDITABLE_CONTACT_ROWS, getFieldLabel, InputType } from './claim-form-constants'

type EditableContactFieldsProps = {
  data: Record<string, unknown>
  schema: Record<string, unknown>
  geoData: ClaimFormGeoDataResponse
  formData?: Record<string, unknown>
  onChange: (updated: Record<string, unknown>) => void
}

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)
  if (!match) {
    return ''
  }
  const [, area, prefix, line] = match
  if (line) {
    return `(${area}) ${prefix}-${line}`
  }
  if (prefix) {
    return `(${area}) ${prefix}`
  }
  if (area) {
    return `(${area}`
  }
  return ''
}

export function EditableContactFields({
  data,
  schema,
  geoData,
  formData,
  onChange,
}: EditableContactFieldsProps) {
  const updateField = useCallback(
    (field: string, value: unknown) => {
      onChange({ ...data, [field]: value })
    },
    [data, onChange],
  )

  const claimant = (formData?.claimant ?? {}) as {
    countryOfResidence?: string
    usCitizen?: boolean
  }
  const isUS = claimant.usCitizen !== false
  const selectedCountry = isUS ? 'United States' : (claimant.countryOfResidence ?? 'United States')

  const regionOptions = useMemo(() => {
    const country = geoData.countries.find((c) => c.name === selectedCountry)
    if (!country) {
      return []
    }
    return country.regions.map((r) => ({ label: r, value: r }))
  }, [geoData, selectedCountry])

  const sectionSchema = (schema as Record<string, unknown>)?.properties as
    | Record<string, unknown>
    | undefined
  const contactSchema = sectionSchema?.contact as Record<string, unknown> | undefined
  const contactProperties = (contactSchema?.properties ?? {}) as Record<string, unknown>
  const requiredFields = ((contactSchema?.required ?? []) as string[]) || []

  const hasSchemaField = (field: string): boolean => field in contactProperties
  const isRequired = (field: string): boolean => requiredFields.includes(field)

  const regionLabel = isUS ? 'State' : 'Province/Region'

  const renderTextField = (
    field: string,
    opts?: { type?: InputType; placeholder?: string; isPhone?: boolean },
  ) => {
    if (!hasSchemaField(field)) {
      return null
    }
    const label = field === 'region' ? regionLabel : getFieldLabel(field)
    const required = isRequired(field)

    if (opts?.isPhone) {
      return (
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="1" color="gray">
            {label} {required && '*'}
          </Text>
          <TextField.Root
            size="1"
            type="tel"
            value={String(data[field] ?? '')}
            onChange={(e) => updateField(field, formatPhoneNumber(e.target.value))}
            placeholder="(123) 456-7890"
            maxLength={14}
          />
        </Flex>
      )
    }

    return (
      <Flex direction="column" gap="1" className="claim-form-field">
        <Text size="1" color="gray">
          {label} {required && '*'}
        </Text>
        <TextField.Root
          size="1"
          type={opts?.type ?? 'text'}
          value={String(data[field] ?? '')}
          onChange={(e) => updateField(field, e.target.value)}
          placeholder={opts?.placeholder}
        />
      </Flex>
    )
  }

  const renderRegionSelect = () => {
    if (!hasSchemaField('region')) {
      return null
    }
    const required = isRequired('region')

    return (
      <Flex direction="column" gap="1" className="claim-form-field">
        <Text size="1" color="gray">
          {regionLabel} {required && '*'}
        </Text>
        <Select.Root
          size="1"
          value={String(data.region ?? '')}
          onValueChange={(v) => updateField('region', v)}>
          <Select.Trigger placeholder="Select..." />
          <Select.Content>
            {regionOptions.map((opt) => (
              <Select.Item key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="3">
      {EDITABLE_CONTACT_ROWS.map(([left, right]) => {
        const isPhoneField = (f: string) =>
          f === 'mobilePhoneNumber' || f === 'secondaryPhoneNumber'

        const renderField = (field: string) => {
          if (field === 'region') {
            return renderRegionSelect()
          }
          if (isPhoneField(field)) {
            return renderTextField(field, { isPhone: true })
          }
          if (field === 'emailAddress') {
            return renderTextField(field, { type: 'email' })
          }
          return renderTextField(field)
        }

        return (
          <div key={left} className="claim-form-two-col">
            {renderField(left)}
            {right ? renderField(right) : <div />}
          </div>
        )
      })}
    </Flex>
  )
}
